/*global define*/
/**
 * Base Backbone controller
 */
define(
	'BaseController',[
		'jquery',
		'backbone',
		'BaseView',
		'BaseModuleModel'
	],
	function ($, Backbone, BaseView, BaseModuleModel) {
		'use strict';

		var ParentClass = BaseView,
			parentClassPrototype = ParentClass.prototype;
		/**
		 * @class
		 * @name BaseController
		 * @extends BaseView
		 */
		return ParentClass.extend(
			/**
			 * @lends BaseController.prototype
			 */
			{
				/**
				 * Name of the current class. Used to build debug messages.
				 * @type {String}
				 */
				className: 'BaseController',

				/**
				 * ModuleModel class, used to convert the given raw module data into an instance of it.
				 * Default is BaseModuleModel.
				 * @type {BaseModuleModel}
				 */
				moduleClass: BaseModuleModel,

				/**
				 * Module's main BaseView class, used to create a new view instance for the current module.
				 * Default is BaseView
				 * @type {BaseView}
				 */
				viewClass: null,

				/**
				 * Main view instance
				 * @type {BaseView}
				 */
				view: null,

				/**
				 * Main module model instance
				 * @type {BaseModuleModel}
				 */
				model: null,

				/**
				 * If true, render the main view and this controller objects at the end of the initialize() method.
				 * @type {Boolean}
				 */
				autoRender: true,

				/**
				 * If true, automatically setup rivets bindings when .render() is called
				 * The rivets data is obtained from .getRivetsData()
				 * @type {Boolean}
				 */
				autoBindRivets: false,

				/**
				 * Customise the controller constructor.
				 * When the module's root DOM element defines additional options (controllerOptions), it will use it to extend the current options parameter.
				 * @constructor
				 * @param {Object} options
				 */
				constructor: function (options) {
					var self = this,
						args = arguments,
						methodName = 'constructor',
						view = options && options.el;

					if (view) {
						var controllerOptions = $(view).data().controllerOptions;
						if (controllerOptions) {
							if (typeof controllerOptions !== 'object') {
								throw new Error(self.report(methodName, 'Error while parsing the controller options string.\ncontrollerOptions=' + controllerOptions + '\nIt should have been a JSON string.'));
							}
							args[0] = $.extend(options, controllerOptions);
						}
					}
					ParentClass.apply(self, args);
				},

				/**
				 * Initialise the controller:
				 *  - Create an inner ModuleModel instance based on this.moduleClass and stores it in this.model
				 *  - Create an inner BaseView instance based on this.viewClass and stores it in this.view
				 *  - Will render the main view and this controller's .render() methods automatically.
				 * @param options
				 */
				initialize: function (options) {
					var self = this,
						methodName = 'initialize',
						currentModuleClass = self.moduleClass,
						currentViewClass = self.viewClass,
						autoRender = self.autoRender,
						model,
						view;

					//DEBUG
					//console.log("Initialize called in Base Controller");

					//DEBUG
					//console.log(self.report(methodName, "initial options = "), options);

					// run parent method
					parentClassPrototype.initialize.apply(self, [].slice.call(arguments));

					if (currentModuleClass) {
						self.coerceAttrToModel('model', currentModuleClass);
					} else {
						throw new Error(self.report(methodName, 'Unable to determine the module model class.'));
					}

					model = self.model;

					if (currentViewClass) {
						view = new currentViewClass({
							el: self.el,
							model: model
						});
						self.view = view;

						if (autoRender) {
							view.render(); // render the view
						}
					}

					//DEBUG
					//console.log('view = ', view);

					if (autoRender) {
						self.render(); // "render" this controller
					}
				},

				/**
				 * Setup rivets during render
				 */
				render: function () {
					var self = this;
					parentClassPrototype.render.apply(self, arguments);
					if (self.autoBindRivets) {
						self.setupRivets();
					}
				},

				/**
				 * Setup rivet bindings using the data from .getRivetsData()
				 * @return {BaseController}
				 */
				setupRivets: function () {
					var self = this;
					// bind rivets hooks with our model
					self.bindRivets(self.getRivetsData());
					return self;
				},

				/**
				 * Returns the data object that will be bound to rivets.
				 * You are supposed to overload it to suit your own needs, but don't forget to call the parent class method first.
				 * By default, this will expose the following objects to rivets:
				 *
				 * {
				 *  module: Current BaseModuleModel instance,
				 *  serviceDataList: Collection of BaseTransferModels,
				 *  transactionModel_{service id}: BaseTransferModel of the corresponding {service id},
				 *  serviceModel_{service id}: BaseServiceModel of the corresponding {service id}
				 * }
				 *
				 * @example
				 * Usage example:
				 * In your controller class, you define:
				 * getRivetsData: function() {
				 *  var ownModel = { ownData: '123' };
				 *  return _.extend(parentClass.prototype.getRivetsData.call(this), ownModel);
				 * }
				 *
				 * Then, when using this method:
				 * var data = controller.getRivetsData();
				 * // data = {
				 *  module: ...,
				 *  serviceDataList: ...,
				 *  transactionModel_###: ...,
				 *  serviceModel_###: ...,
				 *  ownData: '123',
				 * };
				 * controller.bindRivets(data);
				 *
				 * @returns {Object}
				 */
				getRivetsData: function () {
					var self = this,
						model = self.model,
						serviceDataList = model && self.model.get('serviceDataList'),
						transferModelPrefix = 'transferModel_',
						serviceModelPrefix = 'serviceModel_',
						ret = {
							module: model,
							serviceDataList: serviceDataList
						};

					if (serviceDataList) {
						serviceDataList.each(function (transferModel) {
							var transferModelId = transferModel.id;
							//DEBUG
							//console.log('transferModel->id = %o', transferModelId);

							ret[transferModelPrefix + transferModelId] = transferModel;
							ret[serviceModelPrefix + transferModelId] = transferModel.data();
						});
					}

					//DEBUG
					//console.log('getRivetsData(): ', ret);

					return ret;
				},

				/**
				 * Returns the BaseTransferModel contained within the module's service data list, with a given id.
				 * @param {String} [serviceId] Service Id. If no serviceId is given, it'll return the first BaseTransferModel object in the service data list.
				 * @return {BaseTransferModel}
				 */
				getServiceWrapperModel: function (serviceId) {
					var self = this,
						methodName = 'getServiceWrapperModel',
						model = self.model,
						ret = model.getServiceWrapperModel(serviceId);

					return ret;
				},

				/**
				 * Returns ServiceModel contained within the BaseTransferModel object of a given service id.
				 * @param {String} [serviceId] Service Id. If no serviceId is given, it'll return the first BaseTransferModel object in the service data list.
				 * @return {ServiceModel}
				 */
				getServiceModel: function (serviceId) {
					var self = this,
						methodName = 'getServiceModel',
						model = self.model,
						ret = model.getServiceModel(serviceId);

					return ret;
				},

				/**
				 * Returns controller model from a given BaseTransferModel object.
				 * If it cannot be found on the BaseTransferModel, we'll return the controllerModel from the BaseModuleModel (i.e. this.model.get('controllerModel') )
				 * @param {String} [serviceId] Service Id. If no serviceId is given, it'll return the first BaseTransferModel object in the service data list.
				 * @returns {BaseModel}
				 */
				getControllerModel: function (serviceId) {
					var self = this,
						methodName = 'getControllerModel',
						controllerModelAttrName = 'controllerModel',
						model = self.model,
						serviceWrapperModel = model.getServiceWrapperModel(serviceId);

					return serviceWrapperModel.get(controllerModelAttrName) || model.get(controllerModelAttrName);
				},

				/**
				* Triggers an event to notify other interested controller(s) on the page.
				* Optional arguments may be passed to the event handler as well.
				* @param {String} event Name of the event that should be notified
				*/
				triggerControllerEvent: function(event) {
					var moduleManager = require('ModuleManager');
					// simply pass through the optional arguments
					moduleManager.triggerEvent.apply(moduleManager, arguments);
				},

				/**
				* Listens to a specific event which may be triggered by other controller(s) on the page.
				* @param {String} event Name of the event to which the controller should listen to
				* @param {Function} callback Callback function which will be executed when the event is triggered
				*/
				listenToControllerEvent: function(event, callback) {
					var moduleManager = require('ModuleManager');
					moduleManager.listenToEvent(this, event, callback);
				}
			}
		);
	}
);

